import React, { useState, useEffect } from 'react';
import { useAlert } from '../context/AlertContext';
import { useAuth } from '../context/AuthContext';
import { getEvents, deleteEvent, getEventRSVPs } from '../services/eventService';
import Modal from './Modal';
import EventForm from './EventForm';
import RSVPList from './RSVPList';
import PDFUploadForm from './PDFUploadForm';
import PhotoUploadSection from './PhotoUploadSection';
import DashboardStats from './DashboardStats';

const AdminPanel = () => {
  const { showAlert } = useAlert();
  const { logout } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventRSVPs, setEventRSVPs] = useState([]);
  const [rsvpBreakdown, setRsvpBreakdown] = useState(null);
  const [showPastEvents, setShowPastEvents] = useState(false);

  useEffect(() => {
    loadEvents();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadEvents = async () => {
    try {
      setLoading(true);
      const data = await getEvents();
      
      // Fetch RSVPs for each event to populate dashboard stats
      const eventsWithRsvps = await Promise.all(
        data.map(async (event) => {
          try {
            const rsvpData = await getEventRSVPs(event.id);
            return { ...event, rsvps: rsvpData.rsvps || rsvpData };
          } catch (error) {
            console.error(`Error loading RSVPs for event ${event.id}:`, error);
            return { ...event, rsvps: [] };
          }
        })
      );
      
      setEvents(eventsWithRsvps);
    } catch (error) {
      showAlert(error.message || 'Error loading events', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEvent = () => {
    setModalType('add');
    setSelectedEvent(null);
    setShowModal(true);
  };

  const handleManagePDF = () => {
    setModalType('managePdf');
    setShowModal(true);
  };

  const handleEditEvent = (event) => {
    setModalType('edit');
    setSelectedEvent(event);
    setShowModal(true);
  };

  const handleDeleteEvent = async (eventId) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      try {
        await deleteEvent(eventId);
        showAlert('Event deleted successfully!', 'success');
        loadEvents();
      } catch (error) {
        showAlert(error.message || 'Error deleting event', 'error');
      }
    }
  };

  const handleViewRSVPs = async (event) => {
    try {
      const data = await getEventRSVPs(event.id);
      // Backend returns {rsvps: [], total: N, breakdown: {paid, pending, failed}}
      setEventRSVPs(data.rsvps || data); // Fallback if old format
      setRsvpBreakdown(data.breakdown);
      setSelectedEvent(event);
      setModalType('viewRsvps');
      setShowModal(true);
    } catch (error) {
      showAlert(error.message || 'Error loading RSVPs', 'error');
    }
  };

  const handleViewUnpaidContractors = async (event) => {
    try {
      const data = await getEventRSVPs(event.id);
      setEventRSVPs(data.rsvps || data);
      setRsvpBreakdown(data.breakdown);
      setSelectedEvent(event);
      setModalType('viewUnpaidContractors');
      setShowModal(true);
    } catch (error) {
      showAlert(error.message || 'Error loading RSVPs', 'error');
    }
  };

  const handleViewWaitlistedLayton = async (event) => {
    try {
      const data = await getEventRSVPs(event.id);
      setEventRSVPs(data.rsvps || data);
      setRsvpBreakdown(data.breakdown);
      setSelectedEvent(event);
      setModalType('viewWaitlistedLayton');
      setShowModal(true);
    } catch (error) {
      showAlert(error.message || 'Error loading RSVPs', 'error');
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedEvent(null);
    setEventRSVPs([]);
  };

  const handleEventSuccess = () => {
    loadEvents();
  };

  const formatDate = (dateString) => {
    const [year, month, day] = dateString.split('-');
    return `${month}/${day}/${year}`;
  };

  const getEventDate = (dateString) => {
    const [year, month, day] = dateString.split('-');
    return new Date(year, parseInt(month) - 1, parseInt(day));
  };

  const separateEvents = (eventsList) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const upcoming = [];
    const past = [];

    eventsList.forEach((event) => {
      const eventDate = getEventDate(event.date);
      if (eventDate >= today) {
        upcoming.push(event);
      } else {
        past.push(event);
      }
    });

    upcoming.sort((a, b) => getEventDate(a.date) - getEventDate(b.date));
    past.sort((a, b) => getEventDate(b.date) - getEventDate(a.date));

    return { upcoming, past };
  };

  const renderEventCard = (event) => (
    <div key={event.id} className="admin-event-card">
      <div className="admin-event-header">
        <div>
          <h3 className="event-title" style={{ marginBottom: '5px' }}>
            {event.title}
          </h3>
          <p style={{ color: '#666', marginBottom: '10px' }}>
            {formatDate(event.date)} at {event.time}
          </p>
          <p style={{ color: '#666' }}>{event.location}</p>
        </div>
        <div className="admin-event-actions">
          <button
            className="view-rsvps-button"
            onClick={() => handleViewRSVPs(event)}
          >
            Confirmed RSVPs ({event.currentRSVPs || 0})
          </button>
          <button
            className="view-rsvps-button"
            onClick={() => handleViewUnpaidContractors(event)}
          >
            Unpaid Contractors ({
              (event.rsvps || []).filter(
                r => r.registrationType === 'contractor' &&
                     (r.paymentStatus === 'pending' || r.paymentStatus === 'failed')
              ).length
            })
          </button>
          <button
            className="view-rsvps-button"
            onClick={() => handleViewWaitlistedLayton(event)}
          >
            Layton Waitlist ({
              (event.rsvps || []).filter(
                r => r.registrationType === 'employee' && r.employeeStatus === 'waitlisted'
              ).length
            })
          </button>
          <button className="edit-button" onClick={() => handleEditEvent(event)}>
            Edit
          </button>
          <button
            className="delete-button"
            onClick={() => handleDeleteEvent(event.id)}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );

  const getModalTitle = () => {
    switch (modalType) {
      case 'add':
        return 'Add New Event';
      case 'edit':
        return 'Edit Event';
      case 'viewRsvps':
        return 'Event RSVPs';
      case 'viewUnpaidContractors':
        return 'Unpaid Contractor RSVPs';
      case 'viewWaitlistedLayton':
        return 'Waitlisted Layton RSVPs';
      case 'managePdf':
        return 'Manage Tournament PDF';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className="admin-container">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <>
      <div className="admin-container">
        <div className="admin-header">
          <h2 className="admin-title">Event Management</h2>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button className="manage-pdf-button" onClick={handleManagePDF}>
              Manage PDF
            </button>
            <button className="add-event-button" onClick={handleAddEvent}>
              + Add New Event
            </button>
            <button className="logout-button" onClick={logout}>
              Logout
            </button>
          </div>
        </div>

        {/* Dashboard Stats Overview */}
        <DashboardStats events={events} />

        <div className="admin-events-list">
          {events.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">📅</div>
              <h3>No events created yet</h3>
              <p>Click "Add New Event" to create your first tournament</p>
            </div>
          ) : (
            <>
              {(() => {
                const { upcoming, past } = separateEvents(events);

                return (
                  <>
                    {/* Upcoming Events Section */}
                    <div className="events-section">
                      <h3 className="events-section-title">Upcoming Events ({upcoming.length})</h3>
                      {upcoming.length === 0 ? (
                        <p style={{ color: '#999', padding: '20px', textAlign: 'center' }}>No upcoming events</p>
                      ) : (
                        upcoming.map(renderEventCard)
                      )}
                    </div>

                    {/* Past Events Accordion */}
                    {past.length > 0 && (
                      <div className="events-section">
                        <button
                          className="events-accordion-header"
                          onClick={() => setShowPastEvents(!showPastEvents)}
                        >
                          <span style={{ marginRight: '8px' }}>
                            {showPastEvents ? '▼' : '▶'}
                          </span>
                          Past Events ({past.length})
                        </button>
                        {showPastEvents && (
                          <div className="events-accordion-content">
                            {past.map(renderEventCard)}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}
            </>
          )}
        </div>
      </div>

      <PhotoUploadSection events={events} />

      <Modal isOpen={showModal} onClose={handleModalClose} title={getModalTitle()}>
        {(modalType === 'add' || modalType === 'edit') && (
          <EventForm
            event={selectedEvent}
            onClose={handleModalClose}
            onSuccess={handleEventSuccess}
          />
        )}
        {modalType === 'viewRsvps' && (
          <RSVPList rsvps={eventRSVPs} breakdown={rsvpBreakdown} eventId={selectedEvent?.id} />
        )}
        {modalType === 'viewUnpaidContractors' && (
          <RSVPList
            rsvps={eventRSVPs}
            breakdown={rsvpBreakdown}
            eventId={selectedEvent?.id}
            filter="unpaidContractors"
          />
        )}
        {modalType === 'viewWaitlistedLayton' && (
          <RSVPList
            rsvps={eventRSVPs}
            breakdown={rsvpBreakdown}
            eventId={selectedEvent?.id}
            filter="waitlistedLayton"
          />
        )}
        {modalType === 'managePdf' && (
          <PDFUploadForm
            onSuccess={() => {
              showAlert('PDF updated successfully!', 'success');
            }}
            onClose={handleModalClose}
          />
        )}
      </Modal>
    </>
  );
};

export default AdminPanel;
